import 'dayjs/locale/es';

import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import BusinessInput from 'lib/common/BusinessInput';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import { isAdmin, validaRut } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import Calculator from './Calculator';
import WarrantyEditConfirmation from './WarrantyEditConfirmation';

interface EditableSummaryProps {
  warranty: Warranty;
  setIsEditing: (isEditing: boolean) => void;
  refreshWarranty?: () => void;
}

interface WarrantyFormData {
  type: string;
  quotedAmount: string;
  quotedCurrency: 'UF' | 'USD' | 'CLP';
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  tenderId: string;
  principalRut: string;
  unitCode?: string;
  description?: string;
  fogape?: number;
}

dayjs.locale('es');

const EditableSummary: React.FC<EditableSummaryProps> = ({
  warranty,
  setIsEditing,
  refreshWarranty,
}) => {
  const auth = useSelector((state: RootState) => state.auth);
  const userIsAdmin = auth.user ? isAdmin(auth.user) : false;
  const [formData, setFormData] = useState<WarrantyFormData>({
    type: warranty.type ?? '',
    quotedAmount: warranty.quotedAmount?.toString() ?? '',
    quotedCurrency: warranty.quotedCurrency as 'UF' | 'USD' | 'CLP',
    startDate: warranty.startDate ? dayjs(warranty.startDate) : null,
    endDate: warranty.endDate ? dayjs(warranty.endDate) : null,
    tenderId: warranty.tenderId ?? '',
    principalRut: warranty.principalRut ?? '',
    unitCode: warranty.unitCode ?? '',
    description: warranty.description ?? '',
    fogape: warranty.fogape ?? undefined,
  });
  const [rutError, setRutError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: warranty.operationCostVariable,
    fixedRate: warranty.operationCostFixed,
    variableDiscount: warranty.discountRateVariable,
    fixedDiscount: warranty.discountRateFixed,
  });
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, quotedAmount: value }));
  };

  const updateFormData = (updates: Partial<WarrantyFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleOpenConfirmation = () => {
    if (isFormValid()) setConfirmationOpen(true);
  };

  // Validate dates
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      if (formData.endDate.isBefore(formData.startDate, 'day')) {
        setDateError(
          'La fecha de término debe ser posterior a la fecha de inicio',
        );
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    if (formData.principalRut && !validaRut(formData.principalRut)) {
      setRutError('RUT inválido');
    } else {
      setRutError(null);
    }
  }, [formData.principalRut]);

  const isFormValid = () => {
    return (
      formData.type &&
      formData.quotedAmount &&
      formData.quotedCurrency &&
      formData.startDate &&
      formData.endDate &&
      formData.principalRut &&
      !rutError &&
      !dateError
    );
  };

  const isApproved = warranty.status === 'APPROVED';

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ID Licitación"
                  value={formData.tenderId}
                  onChange={(e) => updateFormData({ tenderId: e.target.value })}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Garantía"
                  value={formData.type}
                  onChange={(e) => updateFormData({ type: e.target.value })}
                  required
                >
                  {SGR_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monto Cotizado"
                  type="number"
                  value={parseFloat(formData.quotedAmount) || ''}
                  onChange={handleAmountChange}
                  inputProps={{ step: 'any' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.quotedCurrency}
                      </InputAdornment>
                    ),
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Moneda"
                  value={formData.quotedCurrency}
                  onChange={(e) =>
                    updateFormData({
                      quotedCurrency: e.target.value as 'UF' | 'USD' | 'CLP',
                    })
                  }
                  required
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    label="Fecha Inicio"
                    value={formData.startDate}
                    onChange={(date) => updateFormData({ startDate: date })}
                    minDate={
                      userIsAdmin ? undefined : dayjs().subtract(5, 'day')
                    }
                    maxDate={userIsAdmin ? undefined : dayjs().add(7, 'day')}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    label="Fecha Término"
                    value={formData.endDate}
                    onChange={(date) => updateFormData({ endDate: date })}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!dateError,
                        helperText: dateError,
                        required: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <BusinessInput
                  identifier={formData.principalRut}
                  unitCode={formData.unitCode}
                  onChange={(principalRut, unitCode) =>
                    updateFormData({ principalRut, unitCode })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="fogape-select-label">Fogape</InputLabel>
                  <Select
                    labelId="fogape-select-label"
                    label="Fogape"
                    value={formData.fogape ?? ''}
                    onChange={(e) =>
                      updateFormData({
                        fogape:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      })
                    }
                  >
                    {Array.from({ length: 11 }, (_, i) => (
                      <MenuItem key={i * 10} value={i * 10}>
                        {i * 10}%
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  fullWidth
                  label="Glosa"
                  value={formData.description ?? ''}
                  onChange={(e) =>
                    updateFormData({ description: e.target.value })
                  }
                  multiline
                  minRows={3}
                  maxRows={3}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Calculator
              amount={parseFloat(formData.quotedAmount) || 0}
              startDate={
                formData.startDate && formData.startDate.isValid()
                  ? formData.startDate.toISOString()
                  : undefined
              }
              endDate={
                formData.endDate && formData.endDate.isValid()
                  ? formData.endDate.toISOString()
                  : undefined
              }
              quotedCurrency={formData.quotedCurrency}
              type={formData.type}
              businessIdentifier={warranty.businessIdentifier}
              rateControls={rateControls}
              setRateControls={setRateControls}
              useConcentrationRates={false}
              canEditRates={true}
            />
          </Grid>
        </Grid>

        <Box
          sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}
        >
          <CustomButton color="secondary" onClick={() => setIsEditing(false)}>
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleOpenConfirmation}
            disabled={!isFormValid()}
          >
            Guardar Cambios
          </CustomButton>
        </Box>
      </Box>

      {isConfirmationOpen && (
        <WarrantyEditConfirmation
          open={isConfirmationOpen}
          onClose={() => setConfirmationOpen(false)}
          formData={formData}
          rateControls={rateControls}
          isApproved={isApproved}
          originalWarranty={warranty}
          refreshWarranty={refreshWarranty}
          setIsEditing={setIsEditing}
        />
      )}
    </>
  );
};

export default EditableSummary;
